import React, { useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

type WindowKey = '1h'|'2h'|'4h'|'8h'|'day';

export interface ProjectionChartProps {
  width?: number;
  height?: number;
  step: WindowKey; // time step granularity
  horizonDays?: number; // total horizon in days
  stockStart: number; // finished goods units
  buildableUnits: number; // from materials in product-equivalent units
  salesRatePerHour: number; // units/hour
  leadDays: number; // total time (procurement+inbound+manufacturing)
}

export const ProjectionChart: React.FC<ProjectionChartProps> = ({
  width = 640,
  height = 220,
  step,
  horizonDays = 14,
  stockStart,
  buildableUnits,
  salesRatePerHour,
  leadDays,
}) => {
  const hoursPerStep = useMemo(() => ({ '1h': 1, '2h': 2, '4h': 4, '8h': 8, 'day': 24 }[step]), [step]);
  const totalHours = horizonDays * 24;
  const steps = Math.max(2, Math.floor(totalHours / hoursPerStep));

  const points = useMemo(() => {
    const pts: { t: number; finished: number; materialUnits: number; sold: number }[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i * hoursPerStep;
      const sold = salesRatePerHour * t;
      const finished = Math.max(0, stockStart - sold);
      const materialUnits = buildableUnits; // simple: constant until fabricar
      pts.push({ t, finished, materialUnits, sold });
    }
    return pts;
  }, [steps, hoursPerStep, stockStart, salesRatePerHour, buildableUnits]);

  // Projections
  const timeToStockoutHours = useMemo(() => salesRatePerHour > 0 ? (stockStart + buildableUnits) / salesRatePerHour : Infinity, [salesRatePerHour, stockStart, buildableUnits]);
  const reorderLaunchHours = Math.max(0, timeToStockoutHours - leadDays * 24);

  // Scales
  const pad = 12;
  const w = width;
  const h = height;
  const maxY = Math.max(1, stockStart + buildableUnits);
  const scaleX = (t: number) => pad + (t / totalHours) * (w - 2 * pad);
  const scaleY = (v: number) => h - pad - (v / Math.max(1e-6, maxY)) * (h - 2 * pad);

  const pathLine = (arr: number[], color: string, dash = false) => {
    const d = arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(points[i].t)} ${scaleY(v)}`).join(' ');
    return <path d={d} fill="none" stroke={color} strokeWidth={2} strokeDasharray={dash ? '4 4' : undefined} />;
  };

  // Zones (green/yellow/red)
  const zones = (() => {
    const x0 = scaleX(0);
    const xr = scaleX(Math.min(totalHours, reorderLaunchHours));
    const xs = scaleX(Math.min(totalHours, timeToStockoutHours));
    const xEnd = scaleX(totalHours);
    const y0 = pad;
    const y1 = h - pad;
    return (
      <g>
        {/* Green */}
        {xr > x0 && <rect x={x0} y={y0} width={Math.max(0, xr - x0)} height={y1 - y0} fill="#22c55e12" />}
        {/* Yellow */}
        {xs > xr && <rect x={xr} y={y0} width={Math.max(0, xs - xr)} height={y1 - y0} fill="#f59e0b12" />}
        {/* Red */}
        {xEnd > xs && <rect x={xs} y={y0} width={Math.max(0, xEnd - xs)} height={y1 - y0} fill="#ef444412" />}
      </g>
    );
  })();

  // Reorder marker
  const markerX = scaleX(Math.min(totalHours, reorderLaunchHours));

  // Hover tooltip
  const [hover, setHover] = useState<{ x: number; idx: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    // invert scaleX
    const t = ((x - pad) / (w - 2 * pad)) * totalHours;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round(t / hoursPerStep)));
    setHover({ x: scaleX(points[idx].t), idx });
  };

  const onLeave = () => setHover(null);

  const hoverBox = hover ? (() => {
    const p = points[hover.idx];
    const date = new Date(Date.now() + p.t * 3600 * 1000);
    const fmt = date.toLocaleString();
    return (
      <foreignObject x={Math.max(pad, Math.min(w - 200, hover.x + 8))} y={pad} width={192} height={140} pointerEvents="none">
        <Box sx={{ p: 1, bgcolor: 'white', border: '1px solid', borderColor: 'divider', borderRadius: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{fmt}</Typography>
          <Typography variant="body2">Vendidas: {Math.round(p.sold)}</Typography>
          <Typography variant="body2">Stock terminado: {Math.round(p.finished)}</Typography>
          <Typography variant="body2">MP (eq. unidades): {Math.round(p.materialUnits)}</Typography>
      <Typography variant="caption" color="text.secondary">Tiempo total: {leadDays} días</Typography>
        </Box>
      </foreignObject>
    );
  })() : null;

  const stockoutDate = isFinite(timeToStockoutHours) ? new Date(Date.now() + timeToStockoutHours * 3600 * 1000) : null;

  return (
    <Box sx={{ position: 'relative' }}>
      <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" onMouseMove={onMove} onMouseLeave={onLeave}>
        {/* Axes */}
        <rect x={0} y={0} width={w} height={h} fill="#ffffff" />
        {zones}
        {/* Lines */}
        {pathLine(points.map(p => p.sold), '#384df4', true)}
        {pathLine(points.map(p => p.finished), '#22c55e')}
        {pathLine(points.map(p => p.materialUnits), '#f59e0b')}
        {/* Marker */}
        <line x1={markerX} y1={pad} x2={markerX} y2={h - pad} stroke="#0ea5e9" strokeDasharray="4 4" />
        <text x={markerX + 6} y={pad + 10} fill="#0ea5e9" fontSize={10}>Pedir/fabricar</text>
        {/* Hover */}
        {hover && (
          <g>
            <line x1={hover.x} y1={pad} x2={hover.x} y2={h - pad} stroke="#94a3b8" strokeDasharray="2 3" />
            {hoverBox}
          </g>
        )}
      </svg>
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Legend color="#384df4" label="Ventas (proy.)" dashed />
        <Legend color="#22c55e" label="Stock terminado" />
        <Legend color="#f59e0b" label="MP (eq. unidades)" />
        {stockoutDate && (
          <Typography variant="caption" color="text.secondary">Ruptura estimada: {stockoutDate.toLocaleString()}</Typography>
        )}
      </Stack>
    </Box>
  );
};

const Legend: React.FC<{ color: string; label: string; dashed?: boolean }> = ({ color, label, dashed }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <span style={{ width: 18, height: 0, borderTop: dashed ? `2px dashed ${color}` : `2px solid ${color}`, display: 'inline-block' }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);
